// The activity log: what a person did that no other table keeps (BACKLOG A2,
// ACHIEVEMENTS.md §16, migration 0029).
//
// Achievements read it (A3); nothing does yet. It exists before its reader
// because a record can only reach back as far as the day it started.
//
// TWO HALVES. The stored events (`ActivityEvent`), which the server writes in
// the same batch as the action they describe, and the one thing a client may
// say for itself (`ActivityReport`): that it opened a screen that saves
// nothing, so no server write could have noticed.

import { z } from 'zod';
import { DetailLevelSchema } from '@fsp/theme';

/**
 * Screens a client reports opening, because opening them writes nothing.
 *
 * A FIXED LIST, and short on purpose. A forged report earns somebody an
 * achievement for opening a screen and nothing more, so a client is trusted
 * with exactly this and with no event a table could contradict.
 */
export const ClientFeature = z.enum(['record-book', 'draft-recap', 'trophy-room']);
export type ClientFeature = z.infer<typeof ClientFeature>;

/** What kind of device: from the user agent, or from a client that knows better (`ClientHints.form`). */
export const DeviceForm = z.enum(['phone', 'tablet', 'computer']);
export type DeviceForm = z.infer<typeof DeviceForm>;

/**
 * Every kind the log holds, each with its own `ref`.
 *
 * - `session.started`: a sign-in or a sign-up, never a renewal.
 * - `prefs.changed`: a save of the account's choices that named the look, the
 *   detail level, the theme or the League page's layout; the values it named.
 * - `team.edited`: a manager renamed or recolored their own team.
 * - `lineup.saved`: a manager saved their own lineup; the players it put in.
 * - `client.tried`: a client opened one of `ClientFeature`'s screens, once per
 *   feature per account.
 */
export const ActivityEvent = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('session.started'),
    ref: z.object({
      form: DeviceForm.nullable(),
      /** The device list's label ("Safari on iPhone"), or null with no agent. */
      device: z.string().max(40).nullable(),
    }),
  }),
  z.object({
    kind: z.literal('prefs.changed'),
    ref: z.object({
      look: z.string().max(40).optional(),
      detail: DetailLevelSchema.optional(),
      mode: z.string().max(20).optional(),
      /** The League page arranged (true) or put back to the usual (false). */
      arranged: z.boolean().optional(),
      /** Where some league opens was chosen. */
      opensOn: z.literal(true).optional(),
    }),
  }),
  z.object({
    kind: z.literal('team.edited'),
    ref: z.object({
      teamId: z.string(),
      name: z.boolean(),
      colors: z.boolean(),
      /** A badge picked (B-440). Added 2026-10-18; absent is no. */
      badge: z.boolean().default(false),
      /** A banner picked (PLAN-2026-10-24-team-banners). Added 2026-10-24; absent is no. */
      banner: z.boolean().default(false),
    }),
  }),
  z.object({
    kind: z.literal('lineup.saved'),
    ref: z.object({
      teamId: z.string(),
      window: z.string(),
      /** The cells this save filled with a player who was not there before. */
      in: z.array(
        z.object({ slot: z.string(), slotIndex: z.number().int(), playerId: z.string() }),
      ),
    }),
  }),
  z.object({
    kind: z.literal('client.tried'),
    ref: z.object({ feature: ClientFeature }),
  }),
  z.object({
    // A mock draft finished and kept (MOCK_DRAFT.md §6.1), written in the
    // keep's own batch: Dress Rehearsal counts these.
    kind: z.literal('mock.finished'),
    ref: z.object({ mockId: z.string() }),
  }),
]);
export type ActivityEvent = z.infer<typeof ActivityEvent>;
export type ActivityKind = ActivityEvent['kind'];

/** `POST /api/me/activity`: screens this client opened that save nothing. */
export const ActivityReport = z.object({
  tried: z.array(ClientFeature).min(1).max(ClientFeature.options.length),
});
export type ActivityReportShape = z.infer<typeof ActivityReport>;

/** The answer: every feature this account has reported, from any client, so none is sent twice. */
export const ActivityReported = z.object({ tried: z.array(ClientFeature) });
export type ActivityReportedShape = z.infer<typeof ActivityReported>;
